import { isRoleDeviceId } from '../lib-jitsi-meet/exactCameraDeviceId';
import { getUserSelectedCameraDeviceId } from '../settings/functions.web';
import { shortFingerprint } from '../util/shortFingerprint';
import { getMediaErrorName } from './mediaErrors';
import { cameraLabelForId } from './preferredCamera.web';
/** The server keeps at most this much of a browser message (sanitizeTelemetryValue). */
const MAX_ERROR_MESSAGE_LENGTH = 120;
/** Camera labels in telemetry (cameraLabel, obtainedLabel) share this bound. */
const MAX_LABEL_LENGTH = 60;
/**
 * What a getUserMedia failure says beyond its name, bounded for telemetry.
 * The browser's own message tells a busy camera ("Could not start video
 * source") from other device faults that share NotReadableError; the
 * lib-jitsi-meet wrapper keeps it at gum.error.message. For an
 * OverconstrainedError, the constraint says whether the camera id was stale
 * ('deviceId') or a size could not be met. Never throws: it runs inside the
 * camera chain, where a throw would read as a camera failure.
 *
 * @param {unknown} error - A track-creation rejection.
 * @returns {Object} Constraint and error_message, when present.
 */
export function cameraErrorDetails(error) {
    try {
        const wrapper = error && typeof error === 'object'
            ? error
            : undefined;
        const raw = wrapper?.gum?.error;
        const message = raw?.message || wrapper?.message;
        const source = raw ?? wrapper;
        const constraint = source?.constraint || source?.constraintName;
        return {
            constraint: typeof constraint === 'string' && constraint ? constraint.slice(0, 64) : undefined,
            error_message: typeof message === 'string' && message.trim()
                ? message.trim().slice(0, MAX_ERROR_MESSAGE_LENGTH) : undefined
        };
    }
    catch {
        return {};
    }
}
/**
 * Which camera a request actually opened: a fingerprint of the browser's
 * device id, its label, and whether it is the camera that was named. A
 * mismatch is a fallback or a browser that ignored the request; before
 * 2026-09-23 no event said which camera a room had opened.
 *
 * @param {unknown} tracks - The tracks the request resolved with.
 * @param {string} [requestedId] - The camera id that was named, if any.
 * @returns {Object} Undefined fields when no camera track is among them.
 */
export function describeObtainedCamera(tracks, requestedId) {
    try {
        const camera = (Array.isArray(tracks) ? tracks : []).find(track => track?.getType?.() === 'video'
            && track.getVideoType?.() !== 'desktop');
        const mediaStreamTrack = camera?.getTrack?.();
        const obtainedId = mediaStreamTrack?.getSettings?.()?.deviceId;
        const label = String(mediaStreamTrack?.label || '').slice(0, MAX_LABEL_LENGTH);
        return {
            matchedRequest: obtainedId && requestedId ? obtainedId === requestedId : undefined,
            obtainedCameraRef: obtainedId ? shortFingerprint(obtainedId) : undefined,
            obtainedLabel: label || undefined
        };
    }
    catch {
        return {};
    }
}
/**
 * Telemetry for one camera request, fixed when the request is made.
 *
 * The camera asked for is the one createLocalTracksF will name: the given id,
 * or the settings choice when none is given; roles like 'default' name no
 * camera. Its label comes from the page's device list for that exact id (or
 * the saved choice that paired this id with a label), not from settings,
 * whose label can belong to a different id.
 *
 * @param {IReduxState} state - The redux state (devices and settings).
 * @param {string} [cameraDeviceId] - The id the caller passes to track creation.
 * @returns {Object} The requested camera's ref and label, and builders for
 * the failure and success fields.
 */
export function describeCameraRequest(state, cameraDeviceId) {
    let requestedId;
    let label;
    try {
        const id = cameraDeviceId ?? getUserSelectedCameraDeviceId(state);
        requestedId = id && !isRoleDeviceId(id) ? id : undefined;
        label = requestedId ? cameraLabelForId(state, requestedId)?.slice(0, MAX_LABEL_LENGTH) || undefined : undefined;
    }
    catch {
        // Diagnostics only; a state without devices or settings names nothing.
    }
    const requestedCameraRef = requestedId ? shortFingerprint(requestedId) : undefined;
    return {
        failed: (error) => {
            return {
                error_name: getMediaErrorName(error),
                ...cameraErrorDetails(error),
                requestedCameraRef
            };
        },
        label,
        obtained: (tracks) => {
            return {
                ...describeObtainedCamera(tracks, requestedId),
                requestedCameraRef
            };
        },
        requestedCameraRef
    };
}
